import { t } from '@lingui/macro';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';

export const generateCopyStringBody = ({
  requiredDocs,
  optionalDocs,
}: {
  requiredDocs?: TOfferDataRequirement['actions'];
  optionalDocs?: TOfferDataRequirement['actions'];
}) => {
  let copyStringBodyText: string = '';
  if (requiredDocs) {
    copyStringBodyText = generateBodyTextString({
      docRequirements: requiredDocs,
      newLineBreak: '\n',
    });
  }

  if (optionalDocs && optionalDocs.length > 0) {
    copyStringBodyText = `${copyStringBodyText}${generateBodyTextString({
      docRequirements: optionalDocs,
      newLineBreak: '\n',
    })}`;
  }

  return copyStringBodyText;
};

export const generateBodyTextString = ({
  docRequirements,
  newLineBreak,
}: {
  docRequirements: TOfferDataRequirement['actions'];
  newLineBreak: string;
}) => {
  const text = docRequirements.map((requirement) => {
    const title = requirementsTitle(
      requirement.type,
      requirement.quantifier?.value,
    );

    if (!title) return '';

    return `- ${title} ${
      requirement.is_optional ? '(optional)' : ''
    }${newLineBreak}`;
  });

  return text.join('');
};

export const requirementsTitle = (
  type: string | undefined,
  quantifier: number | undefined,
) => {
  switch (type) {
    case 'company_accounts':
      return 'Most recent filed company accounts';
    case 'bank_statement':
      if (quantifier) {
        return `Last ${quantifier} months of bank statements as PDF files`;
      }
      return 'Last bank statements as PDF files';
    case 'vat_return': {
      const quarter = quantifier && quantifier / 3;
      if (quarter && quarter > 1) {
        return `Last ${quantifier} quarterly VAT returns`;
      }
      return `Last quarter's VAT return`;
    }
    case 'link_open_banking':
      return 'Link your trading bank account';
    case 'via_open_banking':
      return 'Link your business bank account';
    case 'personal_bank_statement':
      return 'Last 3 months personal bank statements';
    case 'management_accounts':
      return 'Current management accounts';
    case 'business_plan':
      return 'Business plan';
    case 'aged_debtor_report':
      return 'Aged debtor report';
    case 'kontoauszuege_hochladen':
      return t`Bank statements`;
    case 'bwa_hochladen':
      return t`Current BWA and SuSa`;
    case 'bwa_susa_hochladen':
      return t`BWAs and SuSas from the last 2 years`;
    case 'gewerbeschein_hochladen':
      return t`Trade certificate`;
    case 'jaehrliche_bwa_susa_hochladen':
      if (quantifier && quantifier > 1) {
        return t`BWA and SuAa of the last ${quantifier} years`;
      }
      return t`BWA and SuAa of the last year`;
    case 'gesellschaftsvertrag_hochladen':
      return t`Articles of association`;
    case 'aktienregisterauszug_hochladen':
      return t`Share registry`;
    default:
      break;
  }
};

export const renderInstruction = (type: string | undefined) => {
  const BR = '\r\n';
  const TAB = '\t';

  switch (type) {
    case 'jaehrliche_bwa_susa_hochladen':
      return t`All BWAs should show company name, sales revenue, purchase of goods, costs including depreciation, interest expenses & preliminary result.${BR}All SuSas should include EB value, cumulative annual values & balance and be submitted in the correct format.${BR}${BR}Example SuSa Format Datev:${BR}${TAB}Correct: all posted accounts or general ledger accounts${BR}${TAB}Incorrect: individual batch, accounts without zero balances`;
    case 'kontoauszuege_hochladen':
      return t`Download the full account statements from your online banking.`;
    default:
      break;
  }
};
